import type { FC, PropsWithChildren } from 'react';
import React from 'react';
import type { Nullable } from 'corgi-types';

import { captureException } from '../utils/error-reporter';

export interface FallbackComponentProps {
  error: Error;
}

interface ErrorBoundaryProps extends PropsWithChildren {
  FallbackComponent?: FC<FallbackComponentProps>;
  onRender?: (error: Error) => void;
}

interface ErrorBoundaryState {
  error: Nullable<Error>;
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  constructor(props) {
    super(props);

    this.state = { error: null };
  }

  componentDidMount() {
    if (this.props.onRender) {
      this.props.onRender(this.state.error);
    }
  }

  componentDidCatch(error) {
    // Wrapped with setTimeout, to prevent issue with NewRelic not available yet.
    setTimeout(() => captureException(error));
    this.setState({ error });
  }

  render() {
    const { children, FallbackComponent } = this.props;
    const { error } = this.state;

    if (!error) return children;

    return FallbackComponent ? <FallbackComponent error={error} /> : null;
  }
}

export default ErrorBoundary;
